import { useCallback } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import clone from 'ramda/es/clone';
import equals from 'ramda/es/equals';
import { steppedHPConfigurations, clinicalStepsBaseline, getStepsFromVersioned } from '../state';
import {
  clinicalSnapshotStepSmids,
  deleteClinicalSnapshot,
  getClinicalSnapshot,
  hasClinicalSnapshot,
  revertStepsToBaseline,
  setClinicalSnapshot,
  stageStepEdit,
  stageStepTransform,
  useClinicalStepWriters,
} from '../clinicalStepStore';
import {
  viewportsConfigurationsState,
  activeHangingProtocolSmidState,
} from '../../ViewportsConfigurations/state';
import {
  clearStepViewportOverrides,
  dropStepViewportOverrides,
  publishStepHangingProtocol,
  restoreStepViewportOverrides,
} from '../../ViewportsConfigurations/viewportStepWriters';
import { FLAT_BUCKET, readBucket } from '../../ViewportsConfigurations/stepBuckets';
import {
  applyProtocolLayouts,
  restoreUserLayoutOverride,
} from '../../Viewer/StudyLoader/viewerLayoutWriters';
import { layoutsForAllCasesState } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { Layouts } from '../../Viewer/StudyLoader/viewerLoaderState';
import type { DehydratedViewportsConfigurations } from '../../ViewportsConfigurations/types';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { createStepToastContentComponent, getDefaultHPToastOptions } from '../Steps/StepToast';
import { HP_TOAST_UNDO_DURATION } from '../Steps/hpStepToastMessages';
import { CLINICAL_STEP_REVERTED_TOAST } from '../Steps/stepsToastFactory';
import { formatLayoutString } from '../steppedHPTypes';
import type { ViewportConfigurationEntry } from '../steppedHPTypes';
import { stepBucketAt } from '../stepUtils';
import { stepViewportConfigsToDehydrated, stepLayoutsToLayouts } from './useStepFormSync';
import { useClinicalViewportClassification } from './useClinicalViewportClassification';

/**
 * Drops null/undefined/empty-array fields so two viewport entries can be compared by effective
 * content rather than by shape. A freshly built entry (e.g. from a thumbnail drop) states every
 * field explicitly, including `criteria: null`/`comparison_index: null`, while a baseline entry
 * from `viewportsArrayToRecord` omits null/empty fields entirely -- so a byte-identical re-drop of
 * the series already showing in a viewport looks different to a naive deep-equal and falsely
 * dirties the step.
 */
function compactViewportEntry(
  entry: ViewportConfigurationEntry
): Partial<ViewportConfigurationEntry> {
  const compacted: Partial<ViewportConfigurationEntry> = {};
  for (const [key, value] of Object.entries(entry)) {
    if (value == null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    (compacted as Record<string, unknown>)[key] = value;
  }
  return compacted;
}

/** True when two viewport entries describe the same effective assignment. Exported for testing. */
export function isViewportEntryEquivalent(
  a: ViewportConfigurationEntry | undefined,
  b: ViewportConfigurationEntry | undefined
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return equals(compactViewportEntry(a), compactViewportEntry(b));
}

/**
 * Whether a string is a well-formed viewport id (`Viewport-{window}-{row}-{col}`).
 *
 * Used to reject a malformed id before it can be treated as a viewport, which a step swap needs
 * because "the step has no entry for it" is a legitimate state (a blank slot) rather than evidence of
 * a bad caller. Deliberately a shape test and not a lookup against the current layout: a blank slot
 * outside the step's recorded viewports is exactly the case that has to work.
 */
function isViewportId(viewportId: string): boolean {
  return /^Viewport-\d+-\d+-\d+$/.test(viewportId);
}

export function useClinicalViewportEditing() {
  const { currentCaseId } = useCurrentCaseContext();
  const activeHPSmid = useRecoilValue(activeHangingProtocolSmidState);
  const { stageStepAt } = useClinicalStepWriters();
  const { enqueueToast } = useToasterDispatch();

  const { classify, pendingClassifications, cancelForStep } = useClinicalViewportClassification();

  // ── Snapshot management ──────────────────────────────────────────────

  const clearSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number) => {
        const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const bucket = stepBucketAt(versioned, stepIndex);
        deleteClinicalSnapshot(currentCaseId, activeHPSmid, bucket);
      },
    [currentCaseId, activeHPSmid]
  );

  const hasSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number): boolean => {
        const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const bucket = stepBucketAt(versioned, stepIndex);
        return hasClinicalSnapshot(currentCaseId, activeHPSmid, bucket);
      },
    [currentCaseId, activeHPSmid]
  );

  const ensureSnapshot = useRecoilCallback(
    ({ snapshot }) =>
      (stepIndex: number) => {
        const caseId = currentCaseId;
        if (!caseId) return;

        const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const bucket = stepBucketAt(versioned, stepIndex);
        if (hasClinicalSnapshot(caseId, activeHPSmid, bucket)) return;

        // Synchronous reads so the snapshot is written before the caller's
        // next write call in the same tick (getPromise would defer to next microtask).
        const vpState = snapshot.getLoadable(viewportsConfigurationsState(caseId)).getValue();
        const allCaseLayouts = snapshot.getLoadable(layoutsForAllCasesState).getValue();
        const caseLayouts = allCaseLayouts[caseId] ?? null;
        const bucketOverrides = readBucket(vpState.byStep, bucket);

        setClinicalSnapshot(caseId, activeHPSmid, bucket, {
          viewports: bucketOverrides ? clone(bucketOverrides) : null,
          layouts: caseLayouts ? clone(caseLayouts) : null,
        });
      },
    [currentCaseId, activeHPSmid]
  );

  // ── Step atom writers ────────────────────────────────────────────────

  /**
   * Writes one viewport's entry into a step, and drops that slot's viewport override.
   *
   * The override drop is what lets the drop render. Gestures that write the case-scoped override layer
   * directly -- the MG/DBT "View 2D/3D" swap, Next Series, the overlay toggle -- file the slot with
   * `wasDropped: true`, and `generateViewportsConfigurations` keeps such an override over the step's
   * base unconditionally. A clinical drop writes only the step, so every later drop into that viewport
   * was saved to the step and never shown: the viewport read as locked (VX-5369). After a drop the step
   * is the authority for the slot, as it is after a swap (see `swapViewportsInStep`).
   *
   * Dropped even when the step write is a no-op, because dragging the step's own series back over a
   * swapped viewport is exactly that case.
   */
  const writeViewportToStep = useRecoilCallback(
    ({ set, snapshot }) =>
      (stepIndex: number, viewportId: string, entry: ViewportConfigurationEntry) => {
        if (currentCaseId) {
          const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
          dropStepViewportOverrides(set, {
            caseId: currentCaseId,
            bucket: stepBucketAt(versioned, stepIndex),
            viewportIds: [viewportId],
          });
        }
        stageStepAt(stepIndex, (step) => {
          if (isViewportEntryEquivalent(step.viewports[viewportId], entry)) return step;
          return {
            ...step,
            viewports: {
              ...step.viewports,
              [viewportId]: entry,
            },
          };
        });
      },
    [currentCaseId, stageStepAt]
  );

  /**
   * Clears one viewport within a step by removing its entry from the step's `viewports` record.
   *
   * An absent entry is how the clinical render path already represents a cleared slot:
   * `rebuildHPFromStep` fills every layout slot with no step entry using
   * `CLEARED_VIEWPORT_CONFIGURATION`, and `viewportsRecordToInputArray` drops cleared entries at
   * save time, so removal round-trips through the backend as "this slot is empty". Writing an entry
   * with null smids instead would leave `series_criteria_source_text` behind for the matcher to
   * re-hang a series into the slot the user just cleared.
   *
   * Being a step write, this also makes the clear visible to the dirty-state comparison, which is
   * what puts the unsaved-changes dot on the step (VX-4733).
   */
  const clearViewportInStep = useCallback(
    (stepIndex: number, viewportId: string) => {
      stageStepAt(stepIndex, (step) => {
        if (!(viewportId in step.viewports)) return step;
        const { [viewportId]: _cleared, ...remaining } = step.viewports;
        return {
          ...step,
          viewports: remaining,
        };
      });
    },
    [stageStepAt]
  );

  /**
   * Swaps two viewports within a step, and drops both slots' viewport overrides.
   *
   * The override drop is half the operation, not tidying up. That bucket is materialised with the
   * whole rendered grid at init, and `saveStep` folds it into the step for every viewport the step
   * does not define -- so after a swap onto an empty viewport, where `swapViewportsInStep` deletes
   * the source entry, the save refilled that slot from the stale override and the series the reader
   * had just moved reappeared where it came from the instant the save completed (VX-5227). After a
   * swap neither slot's old override describes anything: the step is the authority for both.
   */
  const swapViewportsInStep = useRecoilCallback(
    ({ set, snapshot }) =>
      (stepIndex: number, viewportIdA: string, viewportIdB: string) => {
        if (isViewportId(viewportIdA) && isViewportId(viewportIdB) && currentCaseId) {
          const versioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
          dropStepViewportOverrides(set, {
            caseId: currentCaseId,
            bucket: stepBucketAt(versioned, stepIndex),
            viewportIds: [viewportIdA, viewportIdB],
          });
        }
        stageStepAt(stepIndex, (step) => {
          // The VX-4593 second layer, now keyed on the *shape* of the id rather than on whether the
          // step has an entry for it. That bug was a caller passing an `undefined` droppedViewportId
          // (a thumbnail, not a viewport, being Shift-dropped), and presence was standing in for
          // well-formedness -- which is what made a legitimately blank slot unswappable (VX-4931).
          // Checking the shape keeps garbage out while letting a real, empty viewport through.
          if (!isViewportId(viewportIdA) || !isViewportId(viewportIdB)) return step;

          const entryA = step.viewports[viewportIdA];
          const entryB = step.viewports[viewportIdB];

          // Nothing on either side is the only case with nothing to do.
          if (entryA === undefined && entryB === undefined) return step;

          // One side being absent is a swap with a *blank* viewport, which is a real gesture: a step
          // records a blank slot by having no entry for it at all (see clearViewportInStep), so
          // "absent" is data here, not a bad id. Refusing it was VX-4931 -- dragging an image onto a
          // blank viewport in the other window did nothing at all, because in clinical step mode the
          // direct viewport write is suppressed (writeUnlessClinicalStepMode) and this was the only
          // write left.
          //
          // The VX-4593 crash this guard was added for came from writing `undefined` *into* the map,
          // which produces a key whose value is undefined and breaks rebuildHPFromStep on render.
          // Deleting the vacated key instead keeps the "blank means absent" invariant that
          // clearViewportInStep already relies on, so the crash stays fixed without refusing the swap.
          const viewports = { ...step.viewports };
          if (entryB === undefined) {
            delete viewports[viewportIdA];
          } else {
            viewports[viewportIdA] = entryB;
          }
          if (entryA === undefined) {
            delete viewports[viewportIdB];
          } else {
            viewports[viewportIdB] = entryA;
          }

          return { ...step, viewports };
        });
      },
    [currentCaseId, stageStepAt]
  );

  /**
   * Applies a layout change to a single step in clinical mode.
   *
   * All three writes are needed, and the third is what keeps the change step-scoped:
   *
   * 1. `steppedHPConfigurations` — the edit itself, which is what the dirty-state check compares
   *    against `clinicalStepsBaseline` to light up the unsaved-changes dot.
   * 2. `hangingProtocolLayoutsState` — renders the new layout on this paint. The step atom write
   *    also reaches this atom via `useHangingProtocol`'s memo chain, but only in an effect that
   *    fires after paint, so without the direct write the grid renders one frame stale.
   * 3. Clearing `layoutsForAllCasesState[caseId]` — the user-override layer. It is case-scoped and
   *    `localForage`-persisted, and `useLayouts` gives it priority over the step-derived HP
   *    layouts, so leaving a step's layout there applied it to *every* step and survived a reload
   *    as an edit no step was ever marked dirty for (VX-4624). In clinical step mode the active
   *    step owns the layout, so this layer must stay empty.
   */
  const writeLayoutToStep = useRecoilCallback(
    ({ set }) =>
      (stepIndex: number, layouts: Layouts) => {
        stageStepTransform(set, {
          stepIndex,
          transform: (step) => {
            // Merged into the step's own record, not substituted for it (VX-5235). `layouts` is
            // `mergedLayouts` from `Viewer/index.tsx`, which `useLayouts` narrows to the windows
            // currently on screen (`pick(visibleWindowIds, ...)`) -- so a reader working in
            // `/viewer/1` sends `{'1': ...}` alone, and a wholesale replace silently deletes the
            // step's window '0' layout. Nothing surfaces that: their grid renders, the chip goes
            // dirty, the save succeeds, and the other monitor's layout is gone from the stored step.
            // `useStepFormSync.ts:277-282` already preserves step-owned windows this way on the HP
            // form's path; this is the clinical writer matching it.
            const stepLayouts: Record<string, string> = { ...step.layouts };
            for (const [windowId, tuple] of Object.entries(layouts)) {
              stepLayouts[windowId] = formatLayoutString(tuple);
            }
            return { ...step, layouts: stepLayouts };
          },
        });

        applyProtocolLayouts(set, { caseId: currentCaseId, layouts });
      },
    [currentCaseId]
  );

  // ── Revert ───────────────────────────────────────────────────────────

  const revertWithSnapshot = useRecoilCallback(
    ({ set, snapshot: recoilSnapshot }) =>
      (stepIndex: number) => {
        const versioned = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const bucket = stepBucketAt(versioned, stepIndex);
        const snap = getClinicalSnapshot(currentCaseId, activeHPSmid, bucket);
        if (!snap) return;

        // Read baseline synchronously so all set() calls remain in the same
        // Recoil batch. Using getPromise (async) would break the batch boundary,
        // causing dehydratedHangingProtocol to be stale during the first render.
        const baseline = recoilSnapshot.getLoadable(clinicalStepsBaseline).getValue();

        if (baseline?.[stepIndex]) {
          revertStepsToBaseline(set, { indices: new Set([stepIndex]), baseline });

          // Write the baseline step's viewport configs directly to the
          // dehydratedHangingProtocol atom. The step atom update above triggers
          // useHangingProtocol's memo chain, but the effect that writes the
          // recalculated value to dehydratedHangingProtocol fires AFTER paint.
          // Without this direct write, the viewport selector reads the stale
          // atom during the first render and the viewport keeps showing the
          // dropped series instead of reverting to the original.
          if (currentCaseId) {
            publishStepHangingProtocol(set, {
              caseId: currentCaseId,
              bucket,
              dehydrated: stepViewportConfigsToDehydrated(baseline[stepIndex].viewports),
            });
          }
        }

        // Reset user viewport overrides to empty (not null). With byStep[bucket] = {}, the
        // factory merge produces no user overrides for this step.
        const caseId = currentCaseId;
        if (caseId) {
          clearStepViewportOverrides(set, { caseId, bucket });
        }

        // Restore layout atoms from snapshot. When the snapshot has no layout
        // data (layoutsForAllCasesState was empty at snapshot time because the
        // initial layout only lived in hangingProtocolLayoutsState), fall back
        // to deriving layouts from the baseline step data.
        const restoredLayouts = snap.layouts
          ? clone(snap.layouts)
          : baseline?.[stepIndex]?.layouts
            ? stepLayoutsToLayouts(baseline[stepIndex].layouts)
            : null;
        if (restoredLayouts) {
          if (snap.layouts) {
            // The step had a case-scoped override before it was edited (e.g. a layout the user set
            // outside step mode). Put it back exactly as it was.
            restoreUserLayoutOverride(set, { caseId, layouts: restoredLayouts });
          } else {
            // No override existed before the edit, so the layout came from the step. Restoring one
            // here would re-introduce the cross-step, reload-surviving leak (VX-4624); let the
            // step-derived layouts govern instead.
            applyProtocolLayouts(set, { caseId, layouts: restoredLayouts });
          }
        }

        // Cancel any in-flight classifications for this step
        cancelForStep(stepIndex);

        // Remove the snapshot since we've reverted
        deleteClinicalSnapshot(currentCaseId, activeHPSmid, bucket);
      },
    [currentCaseId, activeHPSmid, cancelForStep]
  );

  const revertWithToast = useRecoilCallback(
    ({ snapshot: recoilSnapshot, set }) =>
      (stepIndex: number) => {
        const versionedForBucket = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const bucket = stepBucketAt(versionedForBucket, stepIndex);
        const snap = getClinicalSnapshot(currentCaseId, activeHPSmid, bucket);
        if (!snap) return;

        const caseId = currentCaseId;

        // Capture the full modified state before reverting (for undo).
        // Use synchronous getLoadable to keep all set() calls in the same
        // Recoil batch (async getPromise breaks the batch boundary).
        const versioned = recoilSnapshot.getLoadable(steppedHPConfigurations).getValue();
        const modifiedStep = versioned.steps?.[stepIndex]
          ? clone(versioned.steps[stepIndex])
          : null;

        // Capture pre-revert viewport and layout state so UNDO can restore them
        let modifiedViewports: DehydratedViewportsConfigurations | null = null;
        let modifiedLayouts: Layouts | null = null;
        if (caseId) {
          const vpState = recoilSnapshot
            .getLoadable(viewportsConfigurationsState(caseId))
            .getValue();
          const bucketOverrides = readBucket(vpState.byStep, bucket);
          modifiedViewports = bucketOverrides ? clone(bucketOverrides) : null;
          const allCaseLayouts = recoilSnapshot.getLoadable(layoutsForAllCasesState).getValue();
          modifiedLayouts = allCaseLayouts[caseId] ? clone(allCaseLayouts[caseId]) : null;
        }

        revertWithSnapshot(stepIndex);

        enqueueToast(CLINICAL_STEP_REVERTED_TOAST.message, {
          ...getDefaultHPToastOptions({ autoHideDuration: HP_TOAST_UNDO_DURATION }),
          ContentComponent: createStepToastContentComponent({
            actionLabel: CLINICAL_STEP_REVERTED_TOAST.actionLabel,
            countdownDuration: HP_TOAST_UNDO_DURATION,
            onAction: () => {
              // Restore the modified step back into the step atom
              if (modifiedStep) {
                stageStepEdit(set, {
                  stepIndex,
                  step: modifiedStep,
                });
              }

              // Restore viewport and layout atoms to pre-revert state
              if (caseId && modifiedViewports) {
                restoreStepViewportOverrides(set, { caseId, bucket, overrides: modifiedViewports });
              }
              if (caseId && modifiedLayouts) {
                // modifiedStep above carries the undone layout back into the step atom, so the
                // protocol layer alone is enough to render it -- no user override needed.
                applyProtocolLayouts(set, { caseId, layouts: modifiedLayouts });
              }

              // Restore the snapshot ref so future reverts still work
              setClinicalSnapshot(currentCaseId, activeHPSmid, bucket, snap);

              // Re-fire classifications for viewports missing criteria
              if (modifiedStep) {
                for (const [vpId, vp] of Object.entries(modifiedStep.viewports)) {
                  if (!vp.criteria?.length && vp.series_criteria_source_text) {
                    classify({
                      viewportId: vpId,
                      stepIndex,
                      seriesDescription: vp.series_criteria_source_text,
                      seriesSmid: vp.series_smid ?? null,
                    });
                  }
                }
              }
            },
          }),
        });
      },
    [revertWithSnapshot, enqueueToast, classify, currentCaseId, activeHPSmid]
  );

  const revertAll = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const steps = getStepsFromVersioned(
          snapshot.getLoadable(steppedHPConfigurations).getValue()
        );
        if (steps == null) return;

        // Snapshots are addressed by step smid, so the index `revertWithSnapshot` wants is looked up
        // against the current order. A snapshot whose step is gone -- deleted while dirty -- has no
        // index and is skipped; reverting it would have to guess a position. The store scopes the
        // sweep to this case + HP.
        for (const stepSmid of clinicalSnapshotStepSmids(currentCaseId, activeHPSmid)) {
          const stepIndex = steps.findIndex((step) => (step.smid ?? FLAT_BUCKET) === stepSmid);
          if (stepIndex !== -1) revertWithSnapshot(stepIndex);
        }
      },
    [revertWithSnapshot, currentCaseId, activeHPSmid]
  );

  return {
    // Snapshot management
    ensureSnapshot,
    clearSnapshot,
    hasSnapshot,

    // Step atom writers
    writeViewportToStep,
    clearViewportInStep,
    swapViewportsInStep,
    writeLayoutToStep,

    // Revert
    revertWithSnapshot,
    revertWithToast,
    revertAll,

    // Classification passthrough
    pendingClassifications,
    classify,
    cancelForStep,
  };
}
